import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  isPdfMedia,
  searchMedia,
} from "../../../services/mediaLibraryServices";

import {
  ensureArray,
  normalizeDocument,
  normalizeSearchText,
  sortDocuments,
  uniqueById,
} from "../utils/documentsUtils";

const INITIAL_FILTERS = {
  q: "",
  year: "all",
  category: "all",
  sort: "created_at",
  dir: "DESC",
  limit: 9,
};

export function useDocumentsPage() {
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const deferredSearch = useDeferredValue(filters.q);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const collected = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await searchMedia({
          type: "document",
          is_public: true,
          page: currentPage,
          limit: 120,
          sort: "created_at",
          dir: "DESC",
        });

        collected.push(...ensureArray(response?.items));

        totalPages = Number(response?.totalPages || 1);
        currentPage += 1;
      } while (currentPage <= totalPages);

      const rows = uniqueById(collected)
        .filter((item) => item?.is_public !== false)
        .filter(isPdfMedia)
        .map(normalizeDocument);

      setAllItems(rows);
    } catch (err) {
      console.error("Erreur chargement documents publics:", err);
      setAllItems([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    loadDocuments();
  }, [loadDocuments]);

  const years = useMemo(() => {
    return Array.from(
      new Set(allItems.map((item) => item.year).filter(Boolean))
    ).sort((a, b) => Number(b) - Number(a));
  }, [allItems]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(allItems.map((item) => item.category).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
  }, [allItems]);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(deferredSearch);

    const filtered = allItems.filter((item) => {
      const matchYear =
        filters.year === "all" ? true : item.year === String(filters.year);

      const matchCategory =
        filters.category === "all" ? true : item.category === filters.category;

      const matchSearch = q ? item.searchIndex.includes(q) : true;

      return matchYear && matchCategory && matchSearch;
    });

    return sortDocuments(filtered, filters.sort, filters.dir);
  }, [
    allItems,
    deferredSearch,
    filters.year,
    filters.category,
    filters.sort,
    filters.dir,
  ]);

  const total = filteredItems.length;

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / filters.limit), 1);
  }, [filters.limit, total]);

  const paginatedItems = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * filters.limit;

    return filteredItems.slice(start, start + filters.limit);
  }, [filteredItems, filters.limit, page, totalPages]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const updateFilter = useCallback((name, value) => {
    setPage(1);
    setSelectedDocument(null);

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
    setSelectedDocument(null);
  }, []);

  const setLimit = useCallback((limit) => {
    setPage(1);

    setFilters((previous) => ({
      ...previous,
      limit: Number(limit) || 9,
    }));
  }, []);

  const setSort = useCallback((sort) => {
    setPage(1);

    setFilters((previous) => ({
      ...previous,
      sort,
    }));
  }, []);

  const setDir = useCallback((dir) => {
    setPage(1);

    setFilters((previous) => ({
      ...previous,
      dir,
    }));
  }, []);

  const goToPrevPage = useCallback(() => {
    setPage((current) => Math.max(current - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPage((current) => Math.min(current + 1, totalPages));
  }, [totalPages]);

  const openDocument = useCallback((document) => {
    setSelectedDocument(document);
  }, []);

  const closeDocument = useCallback(() => {
    setSelectedDocument(null);
  }, []);

  const refresh = useCallback(() => {
    setSelectedDocument(null);
    loadDocuments();
  }, [loadDocuments]);

  return {
    loading,
    error,

    items: filteredItems,
    paginatedItems,
    total,
    allTotal: allItems.length,
    totalPages,

    years,
    categories,
    filters,
    page,

    selectedDocument,

    updateFilter,
    resetFilters,
    setLimit,
    setSort,
    setDir,
    goToPrevPage,
    goToNextPage,
    openDocument,
    closeDocument,
    refresh,
  };
}